/**
 * ISO 8601 durations, the way ant returns a membership price's renewal
 * interval.
 *
 * The admin saves a price's interval in seconds (see getPriceInterval in
 * addMembership.tsx), so the database holds e.g. '86400 seconds' and ant hands
 * it back as "PT24H"; intervals stored as Postgres months or years come back as
 * "P1M" / "P1Y". Months and years are read with the same sizes the admin saves
 * them with (30 and 365 days), so a saved interval reads back to the exact
 * seconds it was saved as.
 *
 * Kept free of utils.jsx (which pulls in the Redux store); utils.jsx re-exports
 * the constants below.
 */

export const HOUR_IN_SECONDS = 3600;
export const DAY_IN_SECONDS = HOUR_IN_SECONDS * 24;
export const WEEK_IN_SECONDS = DAY_IN_SECONDS * 7;
export const MONTH_IN_SECONDS = DAY_IN_SECONDS * 30;
export const YEAR_IN_SECONDS = DAY_IN_SECONDS * 365;

const MINUTE_IN_SECONDS = 60;

// P[nY][nM][nW][nD][T[nH][nM][n(.n)S]], case-sensitive as the standard writes it.
const ISO_DURATION =
  /^P(?:(\d+)Y)?(?:(\d+)M)?(?:(\d+)W)?(?:(\d+)D)?(?:T(?:(\d+)H)?(?:(\d+)M)?(?:(\d+(?:\.\d+)?)S)?)?$/;

const COMPONENT_SECONDS = [
  YEAR_IN_SECONDS,
  MONTH_IN_SECONDS,
  WEEK_IN_SECONDS,
  DAY_IN_SECONDS,
  HOUR_IN_SECONDS,
  MINUTE_IN_SECONDS,
  1,
];

/**
 * "PT24H" -> 86400, "P1M" -> 2592000, "P1DT12H" -> 129600. Null when there is
 * no duration or it is not one ("", "P", "PT", "P1DT", "1D", "garbage").
 */
export function isoDurationToSeconds(duration?: string | null): number | null {
  if (!duration || duration.endsWith('T')) return null;
  const match = duration.match(ISO_DURATION);
  if (!match) return null;
  const components = match.slice(1);
  if (components.every((component) => component === undefined)) return null;
  return components.reduce(
    (total, component, index) =>
      component === undefined
        ? total
        : total + Number(component) * COMPONENT_SECONDS[index],
    0
  );
}

export interface Interval {
  intervalValue: number;
  intervalUnits: string;
}

// Largest first: 31536000 s reads as "1 year", not "365 days".
const ADMIN_UNITS: Array<[string, number]> = [
  ['years', YEAR_IN_SECONDS],
  ['months', MONTH_IN_SECONDS],
  ['weeks', WEEK_IN_SECONDS],
  ['days', DAY_IN_SECONDS],
];

/**
 * Seconds -> the { intervalValue, intervalUnits } a membership price uses, in
 * the largest admin unit that divides it exactly (86400 -> 1 day,
 * 2592000 -> 1 month). An interval that is not a whole number of days reads in
 * hours (3600 -> 1 hour). No interval for null, zero, negative or non-finite
 * seconds.
 */
export function secondsToInterval(seconds?: number | null): Interval {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) {
    return { intervalValue: 0, intervalUnits: '' };
  }
  const unit = ADMIN_UNITS.find(([, size]) => seconds % size === 0);
  if (unit) return { intervalValue: seconds / unit[1], intervalUnits: unit[0] };
  return { intervalValue: seconds / HOUR_IN_SECONDS, intervalUnits: 'hours' };
}
